<template>
    <aside class="r-edit__panel">
        <template v-if="!deleting && !deleted">
            <transition-group name="fade-in" mode="out-in">
                <Button key="r-e-close" class="r-edit__close" display="link" @click="closeEditingPanel" aria-label="Cancel Editing"><span class="icon" /></Button>
                <header key="r-e-header" class="r-edit__header">
                    <h2>Editing Resource</h2>
                </header>


                <section key="r-e-sect" class="r-edit__fields">
                    <FormulateForm
                        :name="`res-${name}`"
                        @submit="updateDetails"
                        ref="res-form"
                    >
                        <FormulateInput
                            v-model="convertedName"
                            type="text"
                            label="Name"
                            validation="required"
                        />

                        <FormulateInput
                            v-model="selectedArea"
                            type="select"
                            label="Area"
                            :options="formOptionsAreas"
                        />

                        <FormulateInput
                            v-model="selectedCategory"
                            type="select"
                            label="Category"
                            :options="formOptionsCategories"
                        />

                        <FormulateInput
                            v-if="!addNewGroup"
                            v-model="selectedGroup"
                            name="group"
                            type="select"
                            label="Group"
                            placeholder="Select Group"
                            :options="formOptionsGroups"
                            error-behavior="submit"
                        >
                            <template #label="{ label, id }">
                                <div class="form__label formulate-input-label form__label--toggle">
                                    <label :for="id">{{ label }}</label>
                                    <Button display="link" class="align-right" @click="addNewGroup = true">+ Add New</Button>
                                </div>
                            </template>
                        </FormulateInput>

                        <FormulateInput
                            v-if="addNewGroup"
                            v-model="selectedGroup"
                            name="group"
                            type="text"
                            label="Group"
                            placeholder="New Group Name"
                            error-behavior="submit"
                        >
                            <template #label="{ label, id }">
                                <div class="form__label formulate-input-label form__label--toggle">
                                    <label :for="id">{{ label }}</label>
                                    <Button display="link" class="align-right" @click="addNewGroup = false">Cancel</Button>
                                </div>
                            </template>
                        </FormulateInput>

                        <!-- <FormulateInput
                            v-model="selectedCategory"
                            type="text"
                            label="Category"
                        /> -->

                        <FormulateInput
                            v-model="jurisdiction"
                            type="text"
                            label="Jurisidiction"
                        />

                        <FormulateInput
                            v-model="tags"
                            type="text"
                            validation="required"
                            label="Tags"
                            help="Tags are comma seperated"
                        />

                        <FormulateInput
                            v-model="page"
                            label="Page"
                            :options="pageParentOptions"
                            type="select"
                            help="If the page has child pages, they will appear on selection"
                        />

                        <transition name="fade-in" mode="out-in" appear>
                            <FormulateInput
                                v-if="childPageOptions"
                                v-model="childPage"
                                label="Child Page"
                                :options="childPageOptions"
                                type="select"
                            />

                        </transition>

                        <transition name="fade-in" mode="out-in" appear>
                            <div v-if="page !== 'none'" style="margin-bottom: 3em;">
                                <FormulateInput
                                    v-model="page_category"
                                    label="Page Category / Tab"
                                    type="text"
                                    help="Case sensitive. This will be the 'Tab' the document sits within"
                                />

                                <FormulateInput
                                    v-model="is_page_only"
                                    class="not-bold"
                                    :options="{ is_page_only: 'These resources are to be indexed to pages only, not Areas.' }"
                                    type="customCheckbox"
                                />
                            </div>
                        </transition>

                        <!-- Tag nouns, available if needed. Are more for natural language search -->
                        <!-- <FormulateInput
                            v-model="tags_nouns"
                            type="text"
                            validation="required"
                            label="Tags (Nouns)"
                            help="Tags are comma seperated"
                        /> -->

                        <template v-if="editingResourceData.type === 'document'">
                            <p style="margin-bottom: 1.5em;">
                                <span class="text--label">File URL:</span>
                                <a class="file-url" :href="documentURL" rel="noopener noreferrer" _target="blank">
                                    {{ documentURL }}
                                </a>
                            </p>

                            <FormulateInput
                                v-model="newFile"
                                ref="uploader"
                                type="file"
                                name="file"
                                validation="mime:application/pdf"
                                upload-behavior="delayed"
                            >
                                <template #label="{label, id}">
                                    <label :for="id" class="label--replace">
                                        Replace File: {{ name }}
                                    </label>
                                </template>
                            </FormulateInput>
                        </template>

                        <FormulateInput
                            v-else
                            v-model="url"
                            name="url"
                            class="add-new--full add-new--url"
                            type="text"
                            :label="urlLabel"
                            validation="bail|required|video"
                            :validation-rules="{
                                video: ({ value }) => validateVideoURL(value)
                            }"
                            :validation-messages="{
                                video: `The URL is invalid. Double check you're using a correct Vimeo or YouTube URL`
                            }"
                            error-behavior="submit"
                        />

                        <transition name="fade-in" mode="out-in">
                            <Alert type="error" v-if="updateError">
                                {{ updateError }}
                            </Alert>
                        </transition>

                        <FormulateInput
                            :class="[
                                'btn--full',
                                { 'btn--loading' : loading },
                                { 'btn--success' : uploaded }
                            ]"
                            type="submit"
                            name="submit"
                        >
                            {{ buttonLabel }}
                            <Spinner v-if="loading" />
                        </FormulateInput>


                        <footer class="r-edit__actions">
                            <Button display="link" class="btn--cancel" @click="closeEditingPanel">Cancel</Button>
                            <Button display="link" class="btn--delete" @click="confirmDelete = true" v-if="!confirmDelete">
                                <SVGicon-bin class="btn__icon" /> Delete Resource
                            </Button>

                            <Alert type="warning" v-if="confirmDelete" class="r-edit__warning">
                                <p>Are you sure you want to delete this resource? This cannot be undone.</p>
                                <div class="btn__group">
                                    <Button class="btn--sm" @click="deleteResource">Yes, delete this resource</Button>
                                    <Button class="btn--sm" display="secondary" @click="confirmDelete = false">No, don't delete this resource</Button>
                                </div>
                            </Alert>
                        </footer>
                    </FormulateForm>
                </section>

                <footer key="r-e-ftr" class="r-edit__footer r-edit__actions">
                    <slot name="footer-start" />
                    <slot name="footer" />
                    <slot name="footer-end" />
                </footer>
            </transition-group>
        </template>

        <transition name="fade-in" mode="out-in">
            <div class="r-edit__overlay" v-if="deleting">
                <Spinner />
                <span class="r-edit__truncate">Deleting {{ fileName }}</span>
            </div>
        </transition>

        <transition name="fade-in" mode="out-in">
            <Alert type="success" v-if="deleted" title="Success">
                <p class="r-edit__truncate"><strong>Deleted:</strong> {{ fileName }}</p>

                <p>This panel will automatically close in <strong>{{ timer }}</strong> seconds.</p>
            </Alert>

        </transition>
    </aside>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { parseVideo } from '@/helpers'
import { DOC_URL } from '@/helpers/axios'

export default {
    data () {
        return {
            name: null,
            convertedName: null,
            selectedArea: null,
            selectedCategory: null,
            selectedGroup: null,
            jurisdiction: null,
            tags: null,
            url: null,
            newFile: null,
            confirmDelete: false,
            updateError: null,
            loading: false,
            uploaded: false,
            deleting: false,
            deleted: false,
            timer: 0,
            addNewGroup: false,
            page: 'none',
            childPage: 'none',
            page_category: null,
            is_page_only: false,
        }
    },

    mounted () {
        // we assign the values to the data to utilise V-model. The alternative would be creating a
        // vuex getter / setter for each value
        const {
            area,
            name,
            filename,
            category,
            group,
            jurisdiction,
            tags,
            url,
            page,
            page_category,
            is_page_only,
            childPage
        } = this.editingResourceData

        console.log(this.editingResourceData)

        // we need to find the actual page path based on the ID.
        let pageSelection
        let childSelection

        this.pages.forEach(item => {
            if (item.children && item.children.length > 0) {
                item.children.forEach(child => {
                    if (child.id == page) {
                        pageSelection = item
                        childSelection = child
                        return
                    }
                })
            }

            if (item.id == page) pageSelection = item
        })

        const selectedArea = Object.keys(this.formOptionsAreas).filter(key => this.formOptionsAreas[key] === area)[0]
        const selectedCategory = Object.keys(this.formOptionsCategories).filter(key => this.formOptionsCategories[key] === category)[0]

        this.name = filename
        this.convertedName = name
        this.selectedArea = selectedArea
        this.selectedCategory = selectedCategory
        this.jurisdiction = jurisdiction
        this.tags = tags
        this.url = url
        this.is_page_only = is_page_only
        this.page_category = page_category
        this.childPage = childPage

        const groupIndexInOptions = Object.keys(this.formOptionsGroups).findIndex(key => key == group.toLowerCase())

        if (groupIndexInOptions === -1) {
            this.addNewGroup = true
            this.selectedGroup = group
        } else {
            this.selectedGroup = group.toLowerCase()
        }

        if (pageSelection) this.page = pageSelection.path
        if (childSelection) this.childPage = childSelection.path
    },

    created () {
        document.querySelector('body').style.overflow = 'hidden'
    },

    computed: {
        ...mapState('App', ['pages']),
        ...mapState('Resources', ['editingResourceData']),
        ...mapGetters('Resources', ['formOptionsAreas', 'formOptionsGroups', 'formOptionsCategories']),

        pageParentOptions () {
            // hideFromNav get's submitted to the API, hide_from_nav is what's returned. So we do a check for
            // both since hideFromNav could be the current states object it's referencing.
            return this.pages
                .filter(({ hideFromNav }) => !hideFromNav)
                .filter(({ hide_from_nav }) => hide_from_nav === 'false')
                .reduce((acc, page) => {
                    return { ...acc, [page.path]: page.name }
                }, { 'none': 'No page - uploading for Core Skills' })
        },

        childPageOptions () {
            if (this.page !== 'none') {
                const selectedPage = this.pages.filter(({ path }) => path === this.page)[0]
                if (selectedPage && selectedPage.children && selectedPage.children.length > 0) {
                    return selectedPage.children.reduce((acc, page) => {
                        return { ...acc, [page.path]: page.name }
                    }, { 'none': 'None' })
                }
            }

            return false
        },

        fileName () {
            return this.name ? `${this.name}` : 'Resource'
        },

        urlLabel () {
            return this.editingResourceData.type === 'video'
                ? 'Video URL (YouTube or Vimeo)'
                : 'Resource URL'
        },

        buttonLabel () {
            if (this.loading) return 'Updating Resource'
            if (this.uploaded) return 'Successfully updated'
            return 'Save Changes'
        },

        documentURL () {
            return this.editingResourceData && this.editingResourceData.url
                ? this.editingResourceData.url.replace('C:/inetpub/wwwroot/edmentalhealthdev.acem.org.au/src/uploads/', DOC_URL)
                : null
        }
    },

    methods: {
        ...mapActions('Resources', ['closeEditingPanel', 'deleteGlobalResource', 'updateResource']),

        validateVideoURL (val) {
            if (!this.editingResourceData.type === 'video' && val) return true

            if (!val) return false

            const videoObj = parseVideo(val)
             if (!videoObj.type || videoObj.id.length < 1) {
                return false
            }

            return true
        },

        updateDetails () {
            // this.loading = true
            this.updateError = null

            const config = {
                headers: { 'Content-Type': 'multipart/form-data' }, // file uploads require form-data...
            }

            const area = this.formOptionsAreas[this.selectedArea]
            const category = this.formOptionsCategories[this.selectedCategory]

            let data = new FormData()
            data.append('id', this.editingResourceData.id)
            data.append('filename', this.editingResourceData.filename)
            data.append('name', this.convertedName)
            data.append('area', area)
            data.append('category', category)
            data.append('group', this.selectedGroup)
            data.append('jurisdiction', this.jurisdiction)
            data.append('tags', this.tags)

            let pageID

            const parent = this.page && this.page !== 'none'
                ? this.pages.filter(page => page.path === this.page)[0]
                : null

            const child = this.childPage && this.childPage !== 'none'
                ? parent.children.filter(page => page.path === this.childPage)[0]
                : null

            if (child) pageID = child.id
            else if (parent) pageID = parent.id

            // now we have the ID, we need to pass it with FormData
            if (pageID) data.append('page', pageID)
            if (this.page && this.page_category) data.append('pageCategory', this.page_category)
            if (parent && this.is_page_only) data.append('isPageOnly', true)

            if (this.editingResourceData.type !== 'document') data.append('url', this.url)

            if (this.newFile) {
                data.append('file', this.newFile.fileList[0])
                data.append('processDocument', true)
            } else {
                data.append('processDocument', false)
            }

            // submits the new data
            this.axios.post('/edit-resource', data, config)
                .then(({ data }) => {

                    // We need to modify the data to match the props resourceCard.vue has:
                    data.name = data.title
                    data.type = this.editingResourceData.type
                    data.url = data.url || this.editingResourceData.url

                    if (this.page && this.childPage) data.childPage = this.childPage

                    this.updateResource(data)

                    this.uploaded = true
                    this.setData = null // new data has been saved, we no longer need the old data...

                    setTimeout(() => {
                        this.uploaded = false
                        this.editing = false
                        this.$emit('unlockTabs')
                        this.closeEditingPanel()
                    }, 1500);
                })
                .catch(err => this.handleError(err))
                .finally(() => { this.loading = false })
        },

        deleteResource () {
            this.confirmDelete = false
            this.deleting = true
            this.updateError = null

            const id = this.editingResourceData.id

            this.axios.delete('/delete-resource', { data: { resource: { id }}})
                .then(() => {
                    this.deleted = true
                    this.timer = 3 // we'll let the user know that the panel will close in X seconds

                    setInterval(() => {
                        if (this.timer > 0) this.timer -= 1 // increment the timer by 1 until it's back to 0
                    }, 1000);

                    setTimeout(() => {
                        this.deleted = false
                        this.editing = false
                        this.$emit('unlockTabs')
                        this.closeEditingPanel()
                    }, 3000); // time to close the panel...

                    // we delay this slightly to ensure the close panel animation has animated out
                    setTimeout(() => (
                        this.deleteGlobalResource({id, page: this.page, child: this.childPage })
                    ), 3300)
                })
                .catch(err => this.handleError(err))
                .finally(() => { this.deleting = false })
        },

        handleError (err) {
            this.updateError = err.message || err
        }
    },

    beforeDestroy () { document.querySelector('body').style.overflow = null }
}
</script>
